import React, { useEffect, useMemo, useRef, useState } from "react";

import { apiFetch } from "./api";
import { resolveSiteAssetUrl } from "./contentUtils";
import LegacyPreviewPane from "./LegacyPreviewPane";

function resolvePreviewAssetUrl(site, value, previewSources) {
  if (!value) {
    return value;
  }

  return previewSources?.[value] || resolveSiteAssetUrl(site, value);
}

function resolvePreviewContent(site, content, previewSources) {
  if (!content) {
    return content;
  }

  return {
    ...content,
    services: {
      ...(content.services || {}),
      items: (content.services?.items || []).map((item) => ({
        ...item,
        image: {
          ...(item?.image || {}),
          url: resolvePreviewAssetUrl(site, item?.image?.url, previewSources),
        },
      })),
    },
    media: {
      ...(content.media || {}),
      logoUrl: resolvePreviewAssetUrl(
        site,
        content.media?.logoUrl,
        previewSources,
      ),
      heroImage: {
        ...(content.media?.heroImage || {}),
        url: resolvePreviewAssetUrl(
          site,
          content.media?.heroImage?.url,
          previewSources,
        ),
      },
      aboutImage: {
        ...(content.media?.aboutImage || {}),
        url: resolvePreviewAssetUrl(
          site,
          content.media?.aboutImage?.url,
          previewSources,
        ),
      },
      gallery: (content.media?.gallery || []).map((item) => ({
        ...item,
        url: resolvePreviewAssetUrl(site, item?.url, previewSources),
      })),
    },
  };
}

export default function PreviewPane({
  site,
  content,
  previewSources,
  activeFieldPath,
  onSelectField,
  accessToken,
}) {
  const iframeRef = useRef(null);
  const requestIdRef = useRef(0);
  const fetchedSiteKeyRef = useRef("");
  const [iframeReady, setIframeReady] = useState(false);
  const [templatePreview, setTemplatePreview] = useState({
    mode: "loading",
    html: "",
    reason: "",
    templateRef: null,
  });

  const siteKey =
    site?.site_id && accessToken
      ? `${site.site_id}:${content?.site?.theme || "classic"}:${accessToken.slice(0, 8)}`
      : "";
  const resolvedContent = useMemo(
    () => resolvePreviewContent(site, content, previewSources),
    [site, content, previewSources],
  );

  useEffect(() => {
    fetchedSiteKeyRef.current = "";
    setIframeReady(false);
    setTemplatePreview({
      mode: "loading",
      html: "",
      reason: "",
      templateRef: null,
    });
  }, [siteKey]);

  useEffect(() => {
    if (!content || !site?.site_id || !accessToken || !siteKey) {
      return;
    }

    if (fetchedSiteKeyRef.current === siteKey) {
      return;
    }

    fetchedSiteKeyRef.current = siteKey;
    const currentRequestId = ++requestIdRef.current;

    (async () => {
      try {
        const payload = await apiFetch(`/sites/${site.site_id}/preview`, {
          accessToken,
          method: "POST",
          body: {
            content,
            previewSources,
          },
        });

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setTemplatePreview({
          mode: payload.data?.mode || "fallback",
          html: payload.data?.html || "",
          reason: payload.data?.reason || "",
          templateRef: payload.data?.templateRef || null,
        });
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setTemplatePreview({
          mode: "fallback",
          html: "",
          reason: error.message || "Kunde inte ladda template-preview.",
          templateRef: null,
        });
      }
    })();
  }, [accessToken, content, previewSources, site?.site_id, siteKey]);

  useEffect(() => {
    function handleMessage(event) {
      if (event.data?.type !== "content-creator-preview-focus") {
        return;
      }

      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data.focusPath && onSelectField) {
        onSelectField(event.data.focusPath);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onSelectField]);

  useEffect(() => {
    if (
      templatePreview.mode !== "template" ||
      !iframeReady ||
      !resolvedContent ||
      !iframeRef.current?.contentWindow
    ) {
      return;
    }

    iframeRef.current.contentWindow.postMessage(
      {
        type: "content-creator-preview-update",
        content: resolvedContent,
        activeFieldPath,
      },
      "*",
    );
  }, [activeFieldPath, iframeReady, resolvedContent, templatePreview.mode]);

  if (templatePreview.mode === "template" && templatePreview.html) {
    return (
      <div className="preview-panel shell-card">
        <div className="preview-toolbar">
          <span>Live preview</span>
        </div>
        <iframe
          ref={iframeRef}
          title="Template preview"
          className="preview-frame"
          sandbox="allow-scripts allow-same-origin"
          srcDoc={templatePreview.html}
          onLoad={() => setIframeReady(true)}
        />
      </div>
    );
  }

  return (
    <div className="preview-panel-stack">
      <div className="preview-panel shell-card">
        <div className="preview-toolbar">
          <span>Live preview</span>
          <span className="preview-toolbar__hint">
            {templatePreview.mode === "loading"
              ? "Laddar GitHub-template..."
              : `Fallback-preview används${templatePreview.reason ? `: ${templatePreview.reason}` : ""}`}
          </span>
        </div>
      </div>
      <LegacyPreviewPane
        site={site}
        content={content}
        previewSources={previewSources}
        activeFieldPath={activeFieldPath}
        onSelectField={onSelectField}
      />
    </div>
  );
}
